import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Plus, Users, Image as ImageIcon, ExternalLink, Copy, Trash2, KeyRound, Search, Bell, Inbox, Settings, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../api';
import CreateClientDialog from '../components/CreateClientDialog';
import NotificationsDrawer from '../components/NotificationsDrawer';
import BrandLogo from '../components/BrandLogo';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { admin, logout } = useAuth();
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [selCount, setSelCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [q, setQ] = useState('');

  const load = async () => {
    try {
      const [cs, ns, ss] = await Promise.all([adminApi.listClients(), adminApi.listNotifications(), adminApi.listSelections()]);
      setClients(cs);
      setNotifCount(ns.filter(n => !n.seen).length);
      setSelCount(ss.length);
    } catch (err) { toast.error('Errore caricamento'); }
  };
  useEffect(() => { load(); }, []);

  const copyLink = (c) => {
    const url = `${window.location.origin}/c/${c.slug}`;
    // We don't have plaintext password stored; suggest fetching from notification body if freshly created
    navigator.clipboard.writeText(`Link: ${url}`);
    toast.success('Link copiato. La password è stata inviata nella notifica al momento della creazione.');
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare il cliente "${c.name}" con tutti i suoi lavori?`)) return;
    try { await adminApi.deleteClient(c.id); await load(); toast.success('Cliente eliminato'); }
    catch { toast.error('Errore'); }
  };

  const filtered = clients.filter(c => c.name.toLowerCase().includes(q.toLowerCase()) || c.slug.includes(q.toLowerCase()));
  const totalGalleries = clients.reduce((s, c) => s + (c.gallery_count || 0), 0);
  const totalPhotos = clients.reduce((s, c) => s + (c.photo_count || 0), 0);

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-4">
          <Link to="/admin" className="flex items-center gap-2">
            <BrandLogo size="sm" />
          </Link>
          <div className="ml-auto flex items-center gap-3 text-xs">
            <Link to="/admin/impostazioni" className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-full" title="Impostazioni"><Settings className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Impostazioni</span></Link>
            <button onClick={()=>setNotifOpen(true)} className="relative inline-flex items-center gap-1.5 text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-full">
              <Bell className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Notifiche</span>
              {notifCount > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center px-1">{notifCount}</span>}
            </button>
            <span className="text-slate-400 hidden sm:inline">{admin?.name}</span>
            <button onClick={()=>{logout(); nav('/');}} className="text-slate-400 hover:text-white inline-flex items-center gap-1.5"><LogOut className="w-3.5 h-3.5" /> Esci</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-[#C9A961] uppercase tracking-[0.25em] text-xs font-semibold">Dashboard</p>
            <h1 className="font-display text-4xl md:text-5xl mt-1">Ciao, {admin?.name?.split(' ')[0]}</h1>
            <p className="text-slate-400 mt-1">Gestisci i tuoi clienti e i loro lavori.</p>
          </div>
          <button onClick={()=>setOpen(true)} className="inline-flex items-center gap-2 bg-[#C9A961] hover:bg-white text-black font-semibold px-5 py-2.5 rounded-full transition">
            <Plus className="w-4 h-4" /> Nuovo cliente
          </button>
        </div>

        <div className="grid sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Clienti', value: clients.length, Icon: Users },
            { label: 'Lavori', value: totalGalleries, Icon: ImageIcon },
            { label: 'Foto totali', value: totalPhotos, Icon: KeyRound },
            { label: 'Selezioni ricevute', value: selCount, Icon: Inbox },
          ].map(s => (
            <div key={s.label} className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#C9A961]/15 text-[#C9A961] grid place-items-center"><s.Icon className="w-5 h-5" /></div>
              <div>
                <div className="text-2xl font-bold">{s.value}</div>
                <div className="text-xs text-slate-400">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-5 flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 max-w-md">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Cerca cliente..." className="flex-1 outline-none bg-transparent text-sm text-white placeholder:text-slate-500" />
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white/5 border-2 border-dashed border-white/10 rounded-2xl p-16 text-center">
            <Users className="w-10 h-10 text-[#C9A961] mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-1">{clients.length === 0 ? 'Nessun cliente ancora' : 'Nessun risultato'}</h3>
            <p className="text-slate-400 mb-6 text-sm">{clients.length === 0 ? 'Crea il tuo primo cliente per iniziare.' : 'Prova a modificare la ricerca.'}</p>
            {clients.length === 0 && <button onClick={()=>setOpen(true)} className="inline-flex items-center gap-2 bg-[#C9A961] hover:bg-white text-black font-semibold px-5 py-2.5 rounded-full transition"><Plus className="w-4 h-4" /> Crea cliente</button>}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(c => (
              <div key={c.id} data-testid={`client-card-${c.slug}`} className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-[#C9A961]/50 transition-all">
                <div className="aspect-[4/3] relative overflow-hidden bg-black">
                  {c.cover_url ? <img src={c.cover_url} alt={c.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-slate-600"><ImageIcon /></div>}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-display text-2xl text-white">{c.name}</h3>
                    <p className="text-[11px] text-[#C9A961] font-mono">/c/{c.slug}</p>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-3 text-xs text-slate-400 mb-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {c.gallery_count} lavori</span>
                    <span className="inline-flex items-center gap-1"><KeyRound className="w-3 h-3" /> {c.photo_count} foto</span>
                    <span data-testid={`client-visits-${c.slug}`} className={`inline-flex items-center gap-1 ${c.visit_count > 0 ? 'text-emerald-400' : 'text-slate-500'}`} title={c.last_visit ? `Ultima visita: ${new Date(c.last_visit).toLocaleString('it-IT', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}` : 'Mai visitato'}>
                      <Eye className="w-3 h-3" /> {c.visit_count > 0 ? `${c.visit_count} visite · ${new Date(c.last_visit).toLocaleDateString('it-IT', { day:'2-digit', month:'short' })}` : 'mai visto'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link to={`/admin/clienti/${c.slug}`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#C9A961] hover:bg-white text-black text-xs font-semibold px-3 py-2 rounded-full transition">Gestisci</Link>
                    <a href={`/c/${c.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-2 py-2" title="Apri area cliente"><ExternalLink className="w-3.5 h-3.5" /></a>
                    <button onClick={()=>copyLink(c)} className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-2 py-2" title="Copia link"><Copy className="w-3.5 h-3.5" /></button>
                    <button onClick={()=>remove(c)} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-rose-400 px-2 py-2" title="Elimina cliente"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateClientDialog open={open} onOpenChange={setOpen} onCreated={load} />
      <NotificationsDrawer open={notifOpen} onOpenChange={setNotifOpen} onChanged={load} />
    </div>
  );
};

export default AdminDashboard;
